import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';

/**
 * Selector de emoji con el catálogo Unicode completo (el mismo que ofrece
 * WhatsApp), agrupado por categorías, con búsqueda en español y tonos de piel.
 *
 * Los datos (~400 KB) se cargan la primera vez que se abre, en un chunk aparte,
 * para no engordar la carga inicial de la app.
 */

interface EmojiData {
  group?: number;
  hexcode: string;
  label: string;
  order?: number;
  tags?: string[];
  unicode: string;
  skins?: EmojiData[];
}

interface Grupo {
  id: number;
  nombre: string;
  icono: string;
}

// Mismo orden y numeración que emojibase; el 2 ("componentes") son los
// modificadores sueltos de tono y pelo, que no tienen sentido como portada.
const GRUPOS: Grupo[] = [
  { id: 0, nombre: 'Caras y emociones', icono: '😀' },
  { id: 1, nombre: 'Personas y cuerpo', icono: '👋' },
  { id: 3, nombre: 'Animales y naturaleza', icono: '🐻' },
  { id: 4, nombre: 'Comida y bebida', icono: '🍔' },
  { id: 5, nombre: 'Viajes y lugares', icono: '✈️' },
  { id: 6, nombre: 'Actividades', icono: '⚽' },
  { id: 7, nombre: 'Objetos', icono: '💡' },
  { id: 8, nombre: 'Símbolos', icono: '🔣' },
  { id: 9, nombre: 'Banderas', icono: '🏳️' },
];

const TONOS = [
  { codigo: '', muestra: '✋' },
  { codigo: '1F3FB', muestra: '✋🏻' },
  { codigo: '1F3FC', muestra: '✋🏼' },
  { codigo: '1F3FD', muestra: '✋🏽' },
  { codigo: '1F3FE', muestra: '✋🏾' },
  { codigo: '1F3FF', muestra: '✋🏿' },
];

const normalizar = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

let cache: Promise<EmojiData[]> | null = null;
const cargarEmojis = () =>
  (cache ??= import('emojibase-data/es/compact.json').then(
    (m) => (m.default as EmojiData[]).filter((e) => e.group !== undefined && e.group !== 2),
  ));

/** La variante con el tono elegido, si el emoji la tiene (solo las de un único tono). */
function conTono(emoji: EmojiData, tono: string): EmojiData {
  if (!tono || !emoji.skins) return emoji;
  return (
    emoji.skins.find((s) => {
      const tonos = s.hexcode.match(/1F3F[B-F]/g);
      return tonos !== null && tonos.every((t) => t === tono);
    }) ?? emoji
  );
}

interface Props {
  value: string;
  onChange: (emoji: string) => void;
}

export const EmojiPicker: React.FC<Props> = ({ value, onChange }) => {
  const [abierto, setAbierto] = useState(false);
  const [emojis, setEmojis] = useState<EmojiData[] | null>(null);
  const [error, setError] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [tono, setTono] = useState('');
  const raiz = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  const buscador = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!abierto) return;
    buscador.current?.focus();
    if (!emojis) {
      cargarEmojis()
        .then(setEmojis)
        .catch(() => setError(true));
    }
    const fuera = (e: PointerEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto, emojis]);

  const porGrupo = useMemo(() => {
    if (!emojis) return [];
    // Cada palabra por separado: "bandera espana" encuentra "bandera: España".
    const palabras = normalizar(busqueda).split(/\s+/).filter(Boolean);
    const coincide = (e: EmojiData) => {
      const texto = normalizar([e.label, ...(e.tags ?? [])].join(' '));
      return palabras.every((p) => texto.includes(p));
    };
    return GRUPOS.map((g) => ({
      ...g,
      emojis: emojis.filter((e) => e.group === g.id && coincide(e)).map((e) => conTono(e, tono)),
    })).filter((g) => g.emojis.length > 0);
  }, [emojis, busqueda, tono]);

  const irAGrupo = (id: number) => {
    const destino = lista.current?.querySelector<HTMLElement>(`[data-grupo="${id}"]`);
    if (destino && lista.current) lista.current.scrollTop = destino.offsetTop - lista.current.offsetTop;
  };

  const elegir = (emoji: string) => {
    onChange(emoji);
    setAbierto(false);
  };

  return (
    <div ref={raiz} className="relative w-fit">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        aria-label="Elegir emoji de portada"
        className="font-emoji w-24 h-24 rounded-2xl text-5xl bg-ink-850 border border-ink-700 hover:border-sky-600 transition-colors flex items-center justify-center"
      >
        {value}
      </button>
      <span className="block text-center text-[11px] text-ink-500 mt-1">Cambiar</span>

      {abierto && (
        <div
          role="dialog"
          aria-label="Emojis"
          className="absolute left-0 top-full mt-2 z-30 w-[min(22rem,calc(100vw-2rem))] rounded-2xl bg-ink-900 border border-ink-700 shadow-2xl overflow-hidden"
        >
          <div className="p-2 space-y-2 border-b border-ink-800">
            <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-ink-850 border border-ink-700 focus-within:border-sky-600">
              <Search className="w-4 h-4 text-ink-500 shrink-0" />
              <input
                ref={buscador}
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar emoji"
                className="flex-1 min-w-0 bg-transparent text-sm text-ink-100 placeholder:text-ink-600 outline-none"
              />
            </label>
            <div className="flex gap-0.5 overflow-x-auto">
              {GRUPOS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  title={g.nombre}
                  aria-label={g.nombre}
                  onClick={() => irAGrupo(g.id)}
                  disabled={Boolean(busqueda)}
                  className="font-emoji w-7 h-7 shrink-0 rounded-md text-base hover:bg-ink-800 disabled:opacity-40"
                >
                  {g.icono}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1" role="radiogroup" aria-label="Tono de piel">
              <span className="text-[11px] text-ink-500 mr-1">Tono</span>
              {TONOS.map((t) => (
                <button
                  key={t.codigo || 'amarillo'}
                  type="button"
                  role="radio"
                  aria-checked={tono === t.codigo}
                  onClick={() => setTono(t.codigo)}
                  className={`font-emoji w-7 h-7 rounded-md text-base ${
                    tono === t.codigo ? 'bg-sky-600' : 'hover:bg-ink-800'
                  }`}
                >
                  {t.muestra}
                </button>
              ))}
            </div>
          </div>

          <div ref={lista} className="h-72 overflow-y-auto p-2">
            {error ? (
              <p className="p-4 text-sm text-danger-400">No se pudieron cargar los emojis.</p>
            ) : !emojis ? (
              <div className="h-full flex items-center justify-center text-ink-500">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : porGrupo.length === 0 ? (
              <p className="p-4 text-sm text-ink-500 text-center">Sin resultados.</p>
            ) : (
              porGrupo.map((g) => (
                <section key={g.id} data-grupo={g.id} className="mb-2">
                  <h3 className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                    {g.nombre}
                  </h3>
                  <div className="grid grid-cols-8 gap-0.5">
                    {g.emojis.map((e) => (
                      <button
                        key={e.hexcode}
                        type="button"
                        title={e.label}
                        aria-label={e.label}
                        onClick={() => elegir(e.unicode)}
                        className={`font-emoji aspect-square rounded-lg text-2xl leading-none flex items-center justify-center ${
                          value === e.unicode ? 'bg-sky-600' : 'hover:bg-ink-800'
                        }`}
                      >
                        {e.unicode}
                      </button>
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
